import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import { Icons } from './Icons';

const Dashboard = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('learning');
  const user = auth.currentUser;

  const tabs = [
    { 
      id: 'learning', 
      label: 'Learning Center', 
      icon: <Icons.Book />,
      count: '3 Courses'
    },
    { 
      id: 'profile', 
      label: 'Profile', 
      icon: <Icons.Certificate />,
      count: '2 Certificates'
    },
    { 
      id: 'rankings', 
      label: 'Rankings', 
      icon: <Icons.Trophy />,
      count: 'Top 100'
    },
  ];

  if (!user) {
    navigate('/');
    return null;
  }

  const courses = [
    {
      id: 'crypto-basics',
      title: 'Cryptocurrency Basics',
      description: 'Learn the fundamentals of cryptocurrency trading and blockchain technology.',
      level: 'Beginner',
      lessons: 6,
      duration: '2 Hours',
      progress: 0,
      image: '/images/crypto-basics.jpg'
    },
    {
      id: 'technical-analysis',
      title: 'Technical Analysis',
      description: 'Master the art of reading charts and identifying trading patterns.',
      level: 'Intermediate',
      lessons: 8,
      duration: '3 Hours',
      progress: 0,
      image: '/images/technical-analysis.jpg'
    },
    {
      id: 'risk-management',
      title: 'Risk Management',
      description: 'Learn essential strategies to protect your investments and minimize losses.',
      level: 'Advanced',
      lessons: 5,
      duration: '1.5 Hours',
      progress: 0,
      image: '/images/risk-management.jpg'
    }
  ];

  return (
    <div className="dashboard">
      <aside className="sidebar">
        <div className="user-info">
          <div className="avatar">
            {user.photoURL ? (
              <img src={user.photoURL} alt={user.displayName} />
            ) : (
              <div className="avatar-placeholder">
                {user.displayName?.charAt(0) || user.email?.charAt(0)}
              </div>
            )}
          </div>
          <div className="user-details">
            <h3>{user.displayName || 'Trader'}</h3>
            <div className="badge-container">
              <span className="badge">
                <Icons.Star />
                <span>Beginner</span>
              </span>
              <span className="xp">
                <Icons.Trophy />
                <span>120 XP</span>
              </span>
            </div>
          </div>
        </div>
        
        <nav className="nav-menu">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="icon">{tab.icon}</span>
              <div className="nav-item-content">
                <span className="nav-item-label">{tab.label}</span>
                <span className="nav-item-count">{tab.count}</span>
              </div>
            </button>
          ))}
        </nav>

        <div className="progress-section">
          <div className="progress-header">
            <Icons.Progress />
            <h4>Your Progress</h4>
          </div>
          <div className="progress-bar">
            <div className="progress" style={{ width: '45%' }}></div>
          </div>
          <p className="progress-text">45% Complete</p>
        </div>
      </aside>

      <main className="main-content">
        {activeTab === 'learning' && (
          <div className="learning-center">
            <div className="section-header">
              <h1>Learning Center</h1>
              <div className="view-controls">
                <button className="view-button active">
                  <Icons.Book />
                  <span>All Courses</span>
                </button>
                <button className="view-button">
                  <Icons.Progress />
                  <span>In Progress</span>
                </button>
                <button className="view-button">
                  <Icons.Certificate />
                  <span>Completed</span>
                </button>
              </div>
            </div>
            
            <div className="course-grid">
              {courses.map(course => (
                <div key={course.id} className="course-card">
                  <div className="course-banner">
                    <span className="course-level">
                      <Icons.Book />
                      <span>{course.level}</span>
                    </span>
                    <img src={course.image} alt={course.title} />
                  </div>
                  <div className="course-content">
                    <h3>{course.title}</h3>
                    <p>{course.description}</p>
                    <div className="course-meta">
                      <span>
                        <Icons.Book />
                        {course.lessons} Lessons
                      </span>
                      <span>
                        <Icons.Clock />
                        {course.duration}
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress" style={{ width: `${course.progress}%` }}></div>
                    </div>
                    <button 
                      className="start-course"
                      onClick={() => navigate(`/course/${course.id}`)}
                    >
                      <Icons.Play />
                      <span>Start Course</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="section-header">
              <h1>Your Profile</h1>
              <div className="profile-actions">
                <button className="action-button">
                  <Icons.Certificate />
                  <span>View Certificates</span>
                </button>
                <button className="action-button primary">
                  <Icons.Progress />
                  <span>Track Progress</span>
                </button>
              </div>
            </div>
            
            <div className="profile-stats">
              <div className="stat-card">
                <Icons.Book />
                <h4>Courses Completed</h4>
                <p>3/12</p>
              </div>
              <div className="stat-card">
                <Icons.Clock />
                <h4>Learning Hours</h4>
                <p>24.5</p>
              </div>
              <div className="stat-card">
                <Icons.Trophy />
                <h4>Global Rank</h4>
                <p>#42</p>
              </div>
              <div className="stat-card">
                <Icons.Certificate />
                <h4>Certificates</h4>
                <p>2</p>
              </div>
            </div>

            <div className="achievements">
              <h2>Achievements</h2>
              <div className="achievement-grid">
                <div className="achievement">
                  <Icons.Trophy />
                  <h4>Fast Learner</h4>
                  <p>Completed 3 courses in a week</p>
                </div>
                <div className="achievement">
                  <Icons.Star />
                  <h4>Perfect Score</h4>
                  <p>100% on Crypto Basics quiz</p>
                </div>
                <div className="achievement">
                  <Icons.Certificate />
                  <h4>Early Bird</h4>
                  <p>First 100 users to join</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rankings' && (
          <div className="rankings-section">
            <div className="section-header">
              <h1>Global Rankings</h1>
              <div className="ranking-filters">
                <button className="filter-button active">
                  <Icons.Trophy />
                  <span>All Time</span>
                </button>
                <button className="filter-button">
                  <Icons.Clock />
                  <span>This Month</span>
                </button>
                <button className="filter-button">
                  <Icons.Progress />
                  <span>This Week</span>
                </button>
              </div>
            </div>

            <div className="rankings-list">
              {[...Array(10)].map((_, index) => (
                <div key={index} className={`ranking-item ${index === 2 ? 'current-user' : ''} ${index < 3 ? 'top-3' : ''}`}>
                  <span className="rank">{index + 1}</span>
                  <div className="avatar small">
                    <div className="avatar-placeholder">U</div>
                  </div>
                  <span className="username">User {index + 1}</span>
                  <span className="score">
                    <Icons.Trophy />
                    {1000 - index * 50} XP
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
